// Lightweight fee helpers. In production, prefer fetching these from Firestore team config.

const ZERO_DEC = new Set([
  'bif','clp','djf','gnf','jpy','kmf','krw','mga','pyg','rwf','ugx','vnd','vuv','xaf','xof','xpf'
]);

const STRIPE_FEE_BY_CURRENCY = {
  GBP: { percent: 0.015, fixedMinor: 20 }, // 1.5% + 20p (example)
  USD: { percent: 0.029, fixedMinor: 30 }, // 2.9% + $0.30 (example)
  AUD: { percent: 0.029, fixedMinor: 30 }, // 2.9% + A$0.30 (example)
  NZD: { percent: 0.029, fixedMinor: 30 }, // 2.9% + NZ$0.30 (example)
  CAD: { percent: 0.029, fixedMinor: 30 }, // 2.9% + C$0.30 (example)
  EUR: { percent: 0.029, fixedMinor: 30 }, // 2.9% + €0.30 (example)
};

// FX rates from GBP major units to target major units.
// These are pragmatic defaults for UI/estimation. Override from config when needed.
const FX_RATE_FROM_GBP = {
  GBP: 1,
  USD: 2.0,
  EUR: 1.5,
  AUD: 2.0,
  NZD: 2.0,
  CAD: 2.0,
  CHF: 1.1,
  SEK: 13.0,
  NOK: 13.5,
  DKK: 9.5,
  JPY: 190,
  KRW: 1750,
  VND: 31000,
};

export function toMinorFromMajor(major, currency = 'GBP') {
  if (major === undefined || major === null || major === '') return 0;
  const n = Number(major);
  if (!Number.isFinite(n)) return 0;
  const zero = ZERO_DEC.has(currency.toLowerCase());
  return Math.round(n * (zero ? 1 : 100));
}

export function toMajorFromMinor(minor, currency = 'GBP') {
  const zero = ZERO_DEC.has(currency.toLowerCase());
  const d = zero ? 0 : 2;
  return (Number(minor || 0) / (zero ? 1 : 100)).toFixed(d);
}

/**
 * Returns an object { percent, fixedMinor } for Stripe fees.
 * Ideally read from Firestore: teams/{teamId}/stripe/setup or a global config doc.
 * Fallbacks below are EXAMPLES for dev; adjust to your account's real fees.
 */
export function getStripeFeeConfig({ currency = 'GBP', teamFeeCfg }) {
  if (teamFeeCfg?.stripe?.percent != null || teamFeeCfg?.stripe?.fixedMinor != null) {
    return {
      percent: Number(teamFeeCfg.stripe.percent || 0),       // e.g. 0.015 for 1.5%
      fixedMinor: Number(teamFeeCfg.stripe.fixedMinor || 0), // e.g. 20 for £0.20
    };
  }
  const cur = String(currency || 'GBP').toUpperCase();
  return STRIPE_FEE_BY_CURRENCY[cur] || { percent: 0.029, fixedMinor: 30 };
}

export function estimateStripeFeeMinor(unitPriceMinor, cfg) {
  // Stripe rounds to the smallest currency unit; this simple estimate is fine for UI
  const pct = Math.round(unitPriceMinor * cfg.percent);
  return pct + Number(cfg.fixedMinor || 0);
}

export function grossUpForStripeFeeMinor(netAmountMinor, cfg) {
  const net = Math.max(0, Math.round(Number(netAmountMinor || 0)));
  const percent = Number(cfg?.percent || 0);
  const fixedMinor = Math.max(0, Number(cfg?.fixedMinor || 0));

  if (net <= 0) return 0;
  if (!Number.isFinite(percent) || percent <= 0) {
    return net + fixedMinor;
  }
  if (percent >= 1) {
    return net + fixedMinor;
  }

  let gross = Math.max(net, Math.ceil((net + fixedMinor) / (1 - percent)));

  while (gross - estimateStripeFeeMinor(gross, cfg) < net) {
    gross += 1;
  }
  while (gross > net) {
    const candidate = gross - 1;
    if (candidate - estimateStripeFeeMinor(candidate, cfg) < net) break;
    gross = candidate;
  }

  return gross;
}

function minorUnitFactor(currency = 'GBP') {
  return ZERO_DEC.has(String(currency).toLowerCase()) ? 1 : 100;
}

export function convertGbpMinorToCurrencyMinor(gbpMinor, currency = 'GBP') {
  const cur = String(currency || 'GBP').toUpperCase();
  const baseMinor = Math.max(0, Number(gbpMinor || 0));
  if (cur === 'GBP') return Math.round(baseMinor);

  const rate = Number(FX_RATE_FROM_GBP[cur] || 1);
  const gbpMajor = baseMinor / 100; // GBP has 2 decimals
  // Round to whole major units for cleaner, integer pricing (e.g. €1.50 -> €2).
  const targetMajorRounded = Math.round(gbpMajor * rate);
  const targetMinor = targetMajorRounded * minorUnitFactor(cur);
  return Math.max(0, Math.round(targetMinor));
}

export function getPlatformFeeMinor({ currency = 'GBP', teamFeeCfg, memberNestFeePence }) {
  if (Number.isFinite(memberNestFeePence)) {
    return convertGbpMinorToCurrencyMinor(memberNestFeePence, currency);
  }

  if (Number.isFinite(Number(teamFeeCfg?.memberNestFeePence))) {
    return convertGbpMinorToCurrencyMinor(Number(teamFeeCfg.memberNestFeePence), currency);
  }

  if (teamFeeCfg?.platformFeeMinor != null) {
    return Number(teamFeeCfg.platformFeeMinor || 0);
  }

  // Default: £1.00 in pence, converted to requested currency minor units.
  return convertGbpMinorToCurrencyMinor(100, currency);
}
